import React, { useState, useEffect } from "react";
import axios from "axios";
import { Code2, Copy, Check, ExternalLink, Terminal, Globe, FileCode, Webhook, Loader2, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMBED_SCRIPT = `<!-- Adicione no <head> da sua página -->
<script src="${BACKEND_URL || 'https://seu-dominio.com'}/assinador-sdk.js"></script>

<!-- Botão de assinatura na sua página -->
<div id="btn-assinar"></div>

<script>
  AssinadorDigital.init({
    backendUrl: "${BACKEND_URL || 'https://seu-dominio.com'}",
    onSuccess: function(result) {
      console.log('Documento assinado!', result);
      // result.document_id  — ID do documento
      // result.signer_cn    — Nome do assinante
      // result.sign_type    — CAdES | PAdES | XAdES
      // result.signed_at    — Data/hora
    },
    onError: function(err) {
      console.error('Erro na assinatura:', err);
    }
  });

  // Criar botão no elemento
  AssinadorDigital.createButton(
    document.getElementById('btn-assinar'),
    {
      label: 'Assinar Prontuário',
      signType: 'PAdES',  // PAdES | XAdES | CAdES | auto
    }
  );
</script>`;

const POPUP_CODE = `// Abrir popup de assinatura programaticamente
AssinadorDigital.signDocument(fileBlob, {
  signType: 'PAdES',
  filename: 'receita.pdf',
});`;

const IFRAME_CODE = `<!-- Embed como iframe (modo popup embutido) -->
<iframe
  src="${BACKEND_URL || 'https://seu-dominio.com'}/popup?mode=embed"
  width="480"
  height="640"
  frameborder="0"
  allow="clipboard-write"
  style="border-radius: 12px; border: 1px solid #eee;"
></iframe>`;

const API_UPLOAD = `# 1. Upload do documento
curl -X POST "${BACKEND_URL}/api/documents/upload" \\
  -F "file=@receita.pdf" \\
  -F "sign_type=PAdES"

# Resposta: {"document_id": "uuid", "sign_type": "PAdES", ...}`;

const API_PREPARE = `# 2. Preparar para assinatura (obter hash)
curl -X POST "${BACKEND_URL}/api/sign/prepare/{document_id}"

# Resposta: {"hash": "sha256hex...", "hash_algorithm": "SHA-256", ...}`;

const API_COMPLETE = `# 3. Completar assinatura (enviar assinatura + certificado)
curl -X POST "${BACKEND_URL}/api/sign/complete" \\
  -H "Content-Type: application/json" \\
  -d '{
    "document_id": "uuid",
    "signature_base64": "base64...",
    "certificate_pem": "-----BEGIN CERTIFICATE-----\\n..."
  }'`;

const API_VERIFY = `# Verificar documento assinado
curl -X POST "${BACKEND_URL}/api/verify" \\
  -F "file=@documento.pdf.p7"`;

const AGENT_CODE = `# Instalar dependências do Agente Local
pip install flask flask-cors cryptography python-pkcs11

# Executar o agente (detecta PKCS#11 automaticamente)
python agente_local.py

# Ou especificar biblioteca PKCS#11 manualmente:
python agente_local.py --lib "C:\\Windows\\System32\\eTPkcs11.dll"

# O agente roda em http://localhost:7979`;

export default function IntegrationPage() {
  const [copied, setCopied] = useState(null);

  // Webhook state
  const [webhook, setWebhook] = useState({ url: "", enabled: false, has_secret: false });
  const [webhookSecret, setWebhookSecret] = useState("");
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const [webhookSavedMsg, setWebhookSavedMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/webhook/config`).then((r) => {
      setWebhook({ url: r.data.url || "", enabled: r.data.enabled, has_secret: r.data.has_secret });
    }).catch(() => {});
  }, []);

  const saveWebhook = async () => {
    setWebhookSaving(true);
    setWebhookSavedMsg(null);
    try {
      const body = { url: webhook.url, enabled: webhook.enabled };
      if (webhookSecret) body.secret = webhookSecret;
      const r = await axios.put(`${API}/webhook/config`, body);
      setWebhook({ url: r.data.url || "", enabled: r.data.enabled, has_secret: r.data.has_secret });
      setWebhookSecret("");
      setWebhookSavedMsg({ type: "success", text: "Configuração salva com sucesso" });
      setTimeout(() => setWebhookSavedMsg(null), 3000);
    } catch (e) {
      setWebhookSavedMsg({ type: "error", text: e.response?.data?.detail || e.message });
    } finally {
      setWebhookSaving(false);
    }
  };

  const testWebhook = async () => {
    setWebhookTesting(true);
    setWebhookTestResult(null);
    try {
      const r = await axios.post(`${API}/webhook/test`);
      setWebhookTestResult(r.data);
    } catch (e) {
      setWebhookTestResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setWebhookTesting(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Integração SDK
        </h1>
        <p className="text-gray-500 text-sm mt-1">Integre assinaturas digitais no seu sistema de prontuário</p>
      </div>

      {/* Architecture */}
      <div className="bg-[#163A24] text-white rounded-xl p-6 mb-6">
        <h2 className="font-bold text-lg mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Arquitetura do Sistema</h2>
        <div className="grid grid-cols-3 gap-4 text-center text-xs">
          {[
            { icon: Globe, title: "Seu Sistema", desc: "Prontuário Eletrônico\nWidget JS / Iframe" },
            { icon: Code2, title: "Backend API", desc: "FastAPI + MongoDB\nPreparação e montagem" },
            { icon: Terminal, title: "Agente Local", desc: "Python localhost:7979\nAcesso ao token A3" },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white/10 rounded-lg p-4">
              <Icon size={20} className="text-[#8FBC8F] mx-auto mb-2" />
              <p className="font-semibold">{title}</p>
              <p className="text-[#8FBC8F] mt-1 whitespace-pre-line">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-white/10 rounded-lg">
          <p className="text-xs text-[#8FBC8F]">
            Fluxo: Upload → Hash SHA-256 → Assina no token A3 (chave nunca sai do hardware) → Monta documento assinado
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="space-y-6">
        {/* Widget JS */}
        <Section title="Widget JavaScript (Embed)" icon={Code2}>
          <p className="text-sm text-gray-600 mb-3">
            Adicione um botão de assinatura diretamente na sua página HTML com apenas algumas linhas de código.
          </p>
          <CodeBlock code={EMBED_SCRIPT} codeKey="embed" copied={copied} onCopy={copyToClipboard} />
        </Section>

        {/* Popup */}
        <Section title="Modo Popup Programático" icon={Globe}>
          <p className="text-sm text-gray-600 mb-3">
            Abra o assinador como popup a partir de qualquer evento JavaScript.
          </p>
          <CodeBlock code={POPUP_CODE} codeKey="popup" copied={copied} onCopy={copyToClipboard} />
        </Section>

        {/* Iframe */}
        <Section title="Embed via Iframe" icon={FileCode}>
          <p className="text-sm text-gray-600 mb-3">
            Incorpore o assinador diretamente na sua interface usando um iframe.
          </p>
          <CodeBlock code={IFRAME_CODE} codeKey="iframe" copied={copied} onCopy={copyToClipboard} />
        </Section>

        {/* REST API */}
        <Section title="API REST Direta" icon={Terminal}>
          <p className="text-sm text-gray-600 mb-4">
            Para integrações mais avançadas, use a API REST diretamente.
          </p>
          <div className="space-y-3">
            {[
              { code: API_UPLOAD, key: "api1", label: "1. Upload" },
              { code: API_PREPARE, key: "api2", label: "2. Preparar hash" },
              { code: API_COMPLETE, key: "api3", label: "3. Completar" },
              { code: API_VERIFY, key: "api4", label: "Verificar" },
            ].map(({ code, key, label }) => (
              <div key={key}>
                <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">{label}</p>
                <CodeBlock code={code} codeKey={key} copied={copied} onCopy={copyToClipboard} small />
              </div>
            ))}
          </div>
        </Section>

        {/* Agente Local */}
        <Section title="Configurar Agente Local (A3)" icon={Terminal}>
          <p className="text-sm text-gray-600 mb-3">
            O Agente Local acessa o token/cartão A3 via PKCS#11 e expõe uma API em localhost:7979.
          </p>
          <CodeBlock code={AGENT_CODE} codeKey="agent" copied={copied} onCopy={copyToClipboard} />
          <div className="mt-3 p-3 bg-[#FAF9F6] rounded-lg text-xs text-gray-500">
            <p className="font-semibold text-gray-700 mb-1">Bibliotecas PKCS#11 conhecidas:</p>
            <ul className="space-y-1">
              <li><code className="bg-gray-100 px-1 rounded">eTPkcs11.dll</code> — Gemalto/Thales</li>
              <li><code className="bg-gray-100 px-1 rounded">siecap11.dll</code> — SafeNet eToken</li>
              <li><code className="bg-gray-100 px-1 rounded">bit4ipki.dll</code> — Bit4Id</li>
              <li><code className="bg-gray-100 px-1 rounded">opensc-pkcs11.so</code> — OpenSC (Linux/Mac)</li>
              <li><code className="bg-gray-100 px-1 rounded">cess_pkcs11_64.dll</code> — VaultID CESS Desktop</li>
            </ul>
          </div>
        </Section>

        {/* Webhook config */}
        <Section title="Webhook pós-assinatura" icon={Webhook}>
          <p className="text-sm text-gray-600 mb-3">
            Receba notificações HTTP automáticas no seu sistema sempre que um documento for assinado.
            Os payloads são enviados como POST JSON em <code className="bg-gray-100 px-1 rounded text-xs">application/json</code>.
          </p>
          <div className="space-y-3" data-testid="webhook-config">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">URL do Webhook</label>
              <input
                data-testid="webhook-url-input"
                type="url" value={webhook.url}
                onChange={(e) => setWebhook((w) => ({ ...w, url: e.target.value }))}
                placeholder="https://seu-erp.com.br/webhook/assinatura"
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#163A24]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Secret (opcional · enviado em <code className="text-[10px] bg-gray-100 px-1 rounded">X-Signature-Secret</code>)
              </label>
              <input
                data-testid="webhook-secret-input"
                type="password" value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder={webhook.has_secret ? "•••••••• (já configurado, deixe em branco para manter)" : "Defina um segredo para validar a origem"}
                className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#163A24]"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                data-testid="webhook-enabled-checkbox"
                type="checkbox" checked={webhook.enabled}
                onChange={(e) => setWebhook((w) => ({ ...w, enabled: e.target.checked }))}
                className="w-4 h-4 accent-[#163A24]"
              />
              Webhook ativado
            </label>
            <div className="flex items-center gap-2 pt-2">
              <button
                data-testid="btn-save-webhook"
                onClick={saveWebhook} disabled={webhookSaving}
                className="flex items-center gap-2 bg-[#163A24] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#205234] disabled:opacity-50 transition-colors"
              >
                {webhookSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
              </button>
              <button
                data-testid="btn-test-webhook"
                onClick={testWebhook} disabled={webhookTesting || !webhook.url}
                className="flex items-center gap-2 border border-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {webhookTesting ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} Disparar teste
              </button>
              {webhookSavedMsg && (
                <span className={`text-xs ${webhookSavedMsg.type === "success" ? "text-green-600" : "text-red-600"}`} data-testid="webhook-save-msg">
                  {webhookSavedMsg.text}
                </span>
              )}
            </div>
            {webhookTestResult && (
              <div
                data-testid="webhook-test-result"
                className={`p-3 rounded-md text-xs ${webhookTestResult.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
              >
                {webhookTestResult.ok
                  ? <>✓ Entregue: HTTP {webhookTestResult.status_code} para {webhookTestResult.url}</>
                  : <>✗ Falha: {webhookTestResult.error}</>}
              </div>
            )}
          </div>

          <div className="mt-5">
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Payload de exemplo</p>
            <CodeBlock
              codeKey="webhook-payload"
              copied={copied}
              onCopy={copyToClipboard}
              code={`POST ${webhook.url || "https://seu-erp.com.br/webhook"}
Content-Type: application/json
X-Signature-Secret: <seu-secret>

{
  "event": "document.signed",
  "document_id": "uuid-do-documento",
  "filename": "receita.pdf",
  "signer_cn": "MARIA SILVA:12345678901",
  "sign_type": "PAdES",
  "signed_at": "2026-02-15T10:30:00+00:00",
  "demo_mode": false
}`}
            />
          </div>
        </Section>

        {/* API Docs link */}
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <ExternalLink size={18} className="text-blue-500" />
          <div>
            <p className="font-semibold text-blue-700 text-sm">Documentação completa da API</p>
            <a
              href={`${BACKEND_URL}/docs`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-500 hover:underline"
            >
              {BACKEND_URL}/docs — Swagger UI interativo
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-[#163A24]" />
        <h2 className="font-bold text-[#1A1A1A] text-base" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function CodeBlock({ code, codeKey, copied, onCopy, small }) {
  return (
    <div className="relative group">
      <pre className={`code-block p-4 ${small ? "text-xs" : "text-xs"} rounded-lg overflow-x-auto`}>
        <code>{code}</code>
      </pre>
      <button
        data-testid={`copy-${codeKey}`}
        onClick={() => onCopy(code, codeKey)}
        className="absolute top-3 right-3 p-1.5 bg-white/10 hover:bg-white/20 rounded text-white/60 hover:text-white transition-all opacity-0 group-hover:opacity-100"
        title="Copiar código"
      >
        {copied === codeKey ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
